
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserProfile } from "@/hooks/useUserProfile";

interface DeliveryDriverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driver?: any;
  onSuccess: () => void;
}

export const DeliveryDriverDialog = ({
  open,
  onOpenChange,
  driver,
  onSuccess,
}: DeliveryDriverDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(driver?.name || "");
  const [contact, setContact] = useState(driver?.contact || "");
  const [driverType, setDriverType] = useState(driver?.driver_type || "freelance");
  const { data: profile } = useUserProfile();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) {
      toast.error("Utilisateur non connecté");
      return;
    }
    
    setLoading(true);

    try {
      const driverData = {
        name,
        contact,
        driver_type: driverType,
        owner_id: profile.id,
        is_active: true,
      };

      console.log("Sending driver data:", driverData);

      if (driver) {
        const { error } = await supabase
          .from("delivery_drivers")
          .update(driverData)
          .eq("id", driver.id);

        if (error) throw error;
        toast.success("Livreur mis à jour avec succès");
      } else {
        const { error } = await supabase
          .from("delivery_drivers")
          .insert(driverData);

        if (error) throw error;
        toast.success("Livreur ajouté avec succès");
      }

      onSuccess();
      setName("");
      setContact("");
      setDriverType("freelance");
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erreur lors de l'enregistrement:", error);
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {driver ? "Modifier le livreur" : "Ajouter un livreur"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nom du livreur"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact">Contact</Label>
            <Input
              id="contact"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Numéro de téléphone"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="driver-type">Type de livreur</Label>
            <Select
              value={driverType}
              onValueChange={setDriverType}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner le type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="freelance">Freelance</SelectItem>
                <SelectItem value="company">Entreprise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {driver ? "Modifier" : "Ajouter"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
